/**
 * Click-to-copy text cell.
 *
 * Wraps a text field in a <button> that copies the underlying value
 * to clipboard via ``navigator.clipboard.writeText``. Used in
 * inventory tables so operators don't have to highlight + Ctrl+C
 * every MAC / serial / display_id. Toast confirms the copy.
 *
 * Falls back gracefully when ``navigator.clipboard`` isn't available
 * (e.g. insecure context, older browsers, locked-down configs) —
 * uses ``document.execCommand("copy")`` via a temporary textarea.
 */
import { useCallback } from "react";

import { useToast } from "./Toast";

export function CopyableCell({
  value,
  display,
  className,
  title,
}: {
  /** Raw string copied to the clipboard. */
  value: string | number | null | undefined;
  /** Optional rendered representation (e.g. truncated MAC). Defaults to ``value``. */
  display?: React.ReactNode;
  className?: string;
  title?: string;
}) {
  const toast = useToast();

  const onCopy = useCallback(async () => {
    if (value == null || value === "") return;
    const text = String(value);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Insecure-context fallback. Hidden textarea + execCommand.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.push(`Copied "${text.length > 40 ? text.slice(0, 40) + "…" : text}"`, "success");
    } catch (err) {
      toast.push("Copy failed — check browser permissions", "error");
    }
  }, [value, toast]);

  if (value == null || value === "") {
    return <span className={className}>—</span>;
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      title={title ?? "Click to copy"}
      className={[
        "inline-flex max-w-full items-center rounded px-1 py-0.5",
        "text-left font-mono text-xs",
        "hover:bg-slate-800 hover:text-slate-100",
        "transition-[scale,background-color,color] duration-150 ease-out",
        "active:scale-[0.96]",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500",
        "cursor-pointer",
        className ?? "",
      ].join(" ")}
    >
      {display ?? String(value)}
    </button>
  );
}
